import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Home, Car, Sun, DoorOpen, Trees, ChevronRight, Star, Shield, Clock, Fence, Flame, Droplets, Footprints, LayoutGrid, Layers } from "lucide-react";
import { useFranchise } from "@/context/FranchiseContext";

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_instaquote-cleaning/artifacts/damu7ge2_Logo%20InstaQuote.png";

// Services in official rate card order (1-11)
const services = [
  { id: "siding", name: "House Siding", icon: Home, description: "Restore your home's exterior to like-new condition" },
  { id: "driveway", name: "Driveway", icon: Car, description: "Remove oil stains, dirt, and grime from your driveway" },
  { id: "windows", name: "Exterior Windows", icon: LayoutGrid, description: "Crystal clear windows with DI/RO brush cleaning", note: "Property owner must remove all exterior window screens before cleaning" },
  { id: "gutters", name: "Gutter Cleaning", icon: Droplets, description: "Clear debris and ensure proper drainage from your gutters", note: "Open gutters only - does not include gutters with screens" },
  { id: "entries", name: "Front & Rear Walkways", icon: Footprints, description: "Make a great first impression with clean entryways" },
  { id: "sidewalk", name: "Sidewalk & Street Gutter", icon: DoorOpen, description: "Clean sidewalks and street gutters for curb appeal" },
  { id: "patio", name: "Patio Area", icon: Trees, description: "Bring your outdoor living space back to life" },
  { id: "deck", name: "Deck Cleaning", icon: Layers, description: "Restore composite or stained wood decks" },
  { id: "fence", name: "Fence Cleaning", icon: Fence, description: "Restore your fence to its original beauty" },
  { id: "roof", name: "Roof Cleaning", icon: Sun, description: "Safely clean moss, algae, and debris from your roof" },
  { id: "chimney", name: "Exterior Chimney", icon: Flame, description: "Clean brick, stucco, or concrete chimneys" },
];

const features = [
  { icon: Star, title: "5-Star Service", desc: "Trusted by 500+ homeowners" },
  { icon: Shield, title: "Fully Insured", desc: "Licensed, bonded & insured for your peace of mind" },
  { icon: Clock, title: "Quick Turnaround", desc: "Most jobs completed in 1-2 days" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { franchise, getQuoteUrl } = useFranchise();
  
  // Capture campaign code from URL and store in sessionStorage
  useEffect(() => {
    const campaignCode = searchParams.get("campaign");
    if (campaignCode) {
      sessionStorage.setItem("campaign_code", campaignCode);
      // Campaign tracking stored for quote attribution
    }
  }, [searchParams]);

  const goToQuote = () => {
    window.scrollTo(0, 0);
    // Pass campaign code to quote page if present
    const campaignCode = sessionStorage.getItem("campaign_code");
    const quoteUrl = getQuoteUrl();
    if (campaignCode) {
      navigate(`${quoteUrl}?campaign=${campaignCode}`);
    } else {
      navigate(quoteUrl);
    }
  };

  // Use franchise data or defaults
  const logoUrl = franchise?.logo_url || DEFAULT_LOGO;
  const franchiseName = franchise?.name || "Rolling Suds";
  const displayName = franchise?.display_name || "Naperville - Elmhurst";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center gap-2">
              <img 
                src={logoUrl} 
                alt={franchiseName} 
                className="h-20 w-auto"
                style={{ background: 'transparent' }}
              />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-slate-600 hover:text-blue-600 transition-colors font-medium">
                Services
              </a>
              <a href="#why-us" className="text-slate-600 hover:text-blue-600 transition-colors font-medium">
                Why Us
              </a>
              <Button
                onClick={goToQuote}
                className="btn-primary"
                data-testid="nav-get-quote-btn"
              >
                Get Instant Quote
              </Button>
            </div>
            <Button
              onClick={goToQuote}
              className="md:hidden btn-primary !px-4"
              data-testid="mobile-get-quote-btn"
            >
              Get Quote
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                DuPage County Area
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6" style={{ fontFamily: 'Outfit' }}>
                Exterior Cleaning
                <span className="text-blue-600"> Made Simple</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-lg">
                Get an instant, personalized quote for your property. See your home, select your services, and know exactly what you'll pay.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={goToQuote}
                  className="btn-primary text-lg h-14 px-10"
                  data-testid="hero-get-quote-btn"
                >
                  Get Your Free Quote
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="btn-secondary text-lg h-14"
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-view-services-btn"
                >
                  View Services
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in-up stagger-2 hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/5353883/pexels-photo-5353883.jpeg?auto=compress&cs=tinysrgb&w=1200"
                  alt="Suburban home in Chicago suburbs"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Star className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900">500+ Happy Customers</p>
                  <p className="text-sm text-slate-500">In DuPage County</p>
                  <p className="text-xs text-blue-600 italic mt-1">"Making the Chicago suburbs shine, one place at a time"</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-medium uppercase tracking-wider text-sm">Our Services</span>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mt-4 tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Everything Your Home Needs
            </h2>
            <p className="text-slate-600 mt-4 text-lg max-w-2xl mx-auto">
              From driveways to rooftops, we've got your exterior cleaning covered
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div
                key={service.id}
                className={`service-card p-8 group animate-fade-in-up stagger-${idx + 1}`}
                data-testid={`service-card-${service.id}`}
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
                  <service.icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>
                  {service.name}
                </h3>
                <p className="text-slate-600">{service.description}</p>
                {service.note && (
                  <p className="text-xs text-amber-600 mt-2 italic">* {service.note}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before & After Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-medium uppercase tracking-wider text-sm">See The Results</span>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mt-4 tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Before & After
            </h2>
            <p className="text-slate-600 mt-4 text-lg max-w-2xl mx-auto">
              Real transformations from real DuPage County homes. See what professional cleaning can do for your property.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Siding Before/After */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <img
                src="https://customer-assets.emergentagent.com/job_instaquote-cleaning/artifacts/fbyihzsr_Before%20After%20Siding.jpeg"
                alt="House siding before and after cleaning"
                className="w-full h-[350px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <h3 className="text-white text-xl font-bold">House Siding</h3>
                <p className="text-white/80 text-sm mt-1">Removes mold, mildew & years of buildup</p>
              </div>
              <div className="absolute top-4 left-4">
                <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">BEFORE</span>
              </div>
              <div className="absolute top-4 right-4">
                <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">AFTER</span>
              </div>
            </div>
            
            {/* Driveway Before/After */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <img
                src="https://customer-assets.emergentagent.com/job_instaquote-cleaning/artifacts/lusjh2zr_Before%20After%20Driveway.JPG"
                alt="Driveway before and after cleaning"
                className="w-full h-[350px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <h3 className="text-white text-xl font-bold">Driveway</h3>
                <p className="text-white/80 text-sm mt-1">Restores original color & curb appeal</p>
              </div>
              <div className="absolute top-4 left-4">
                <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">BEFORE</span>
              </div>
              <div className="absolute top-4 right-4">
                <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">AFTER</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section id="why-us" className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-blue-600 font-medium uppercase tracking-wider text-sm">Why Choose Us</span>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mt-4 tracking-tight" style={{ fontFamily: 'Outfit' }}>
                The Rolling Suds Difference
              </h2>
              <p className="text-slate-600 mt-6 text-lg leading-relaxed">
                We're not just another pressure washing company. We're your neighbors, committed to making DuPage County homes shine.
              </p>
              <div className="mt-10 space-y-6">
                {features.map((feature) => (
                  <div key={feature.title} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-lg">{feature.title}</h3>
                      <p className="text-slate-600 mt-1">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-blue-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
                  Ready to transform your property?
                </h3>
                <p className="text-blue-100 mb-6">
                  Get your instant quote in under 2 minutes. See exactly what your home needs and how much it costs.
                </p>
                <Button
                  onClick={goToQuote}
                  className="bg-white text-blue-600 hover:bg-blue-50 h-12 px-8 rounded-full font-semibold"
                  data-testid="cta-get-quote-btn"
                >
                  Start Your Quote
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src={logoUrl} 
                alt={franchiseName} 
                className="h-16 w-auto"
              />
            </div>
            <p className="text-slate-400 text-sm">
              &copy; {new Date().getFullYear()} {franchiseName}. All rights reserved.
            </p>
            <a
              href="/admin/login"
              className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
              data-testid="footer-admin-link"
            >
              Admin Portal
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
